// Vendor record foundations — Overview tab on the Vendor registry detail
// page. Identity + contact profile (editable, Vendors.Update). The portal
// account card + invite moved to the dedicated "Portal account" tab
// (party-surface WS-3, the PartyDetailPage precedent).
//
// The second card used to be an "Onboarding & verification" placeholder
// promising "Territory, ownership, onboarded date, background check,
// insurance verification, and W-9 status … in Vendor Program v3.0". Three of
// those six now exist, as manual stages one tab away, and the other three
// (territory, ownership, onboarded date) have no column, no DTO member and
// no endpoint. It is replaced by a summary of the readiness data that
// actually exists rather than a fresh promise about data that does not.

import { useState } from "react";
import { Button, Card } from "@/design-system/components/ui";
import type { VendorDto } from "@/api/portfolioAdmin/types";
import { useAuth } from "@/hooks/useAuth";
import { displayEmail, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";
import { RegistryStatusPill } from "@/components/portfolioAdmin/registry/RegistryStatusPill";
import { VendorEditDrawer } from "../VendorEditDrawer";
import { VendorReadinessCard } from "../VendorReadinessCard";

export function VendorOverviewTab({ vendor }: { vendor: VendorDto }) {
  const auth = useAuth();
  const canEdit =
    auth.hasRoleCode("E1-VM") ||
    auth.hasRoleCode("E5") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Vendors.Update");

  const [editing, setEditing] = useState(false);

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-space-3"
      data-testid="vendor-overview-tab"
    >
      <Card className="flex flex-col gap-space-2">
        <div className="flex items-start justify-between gap-space-2">
          <h3 className="font-display font-bold text-h3 m-0">Identity &amp; contact</h3>
          {canEdit && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="vendor-edit-button"
            >
              Edit details
            </Button>
          )}
        </div>
        <dl className="grid grid-cols-[160px_1fr] gap-y-space-1 gap-x-space-3 text-small m-0">
          <dt className="text-gray-mid">Name</dt>
          <dd className="m-0">{vendor.name}</dd>
          <dt className="text-gray-mid">Type</dt>
          <dd className="m-0">{vendor.vendorType ?? "—"}</dd>
          <dt className="text-gray-mid">Status</dt>
          <dd className="m-0">
            <RegistryStatusPill status={vendor.status} />
          </dd>
          {vendor.isRegulated && (
            <>
              <dt className="text-gray-mid">Billing</dt>
              <dd className="m-0" data-testid="vendor-regulated-indicator">
                Regulated — borrower-paid
              </dd>
            </>
          )}
          <dt className="text-gray-mid">Contact</dt>
          <dd className="m-0" data-testid="vendor-contact-name">
            {vendor.contactName ?? "—"}
          </dd>
          <dt className="text-gray-mid">Email</dt>
          <dd className="m-0" data-testid="vendor-contact-email">
            {displayEmail(vendor.email) ??
              (vendor.email ? (
                <span className="text-gray-mid">{NO_EMAIL_ON_FILE}</span>
              ) : (
                "—"
              ))}
          </dd>
          <dt className="text-gray-mid">Phone</dt>
          <dd className="m-0">{vendor.phone ?? "—"}</dd>
          <dt className="text-gray-mid">Address</dt>
          <dd className="m-0">{vendor.address ?? "—"}</dd>
          <dt className="text-gray-mid">Notes</dt>
          <dd className="m-0 whitespace-pre-wrap">{vendor.notes ?? "—"}</dd>
        </dl>
      </Card>

      <VendorReadinessCard vendorId={vendor.id} />

      {editing && (
        <VendorEditDrawer
          vendor={vendor}
          open={editing}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}
